/**
 * SpawnOptimizer.js - Optimizes player spawn locations
 * 
 * This module helps find optimal spawn locations by:
 * 1. Finding relatively flat areas
 * 2. Ensuring chunks can be loaded quickly at spawn point
 * 3. Ensuring a safe starting area for the player
 */

import * as THREE from 'three';

/**
 * Creates a spawn optimizer
 * @param {Object} config - Configuration
 * @returns {Object} - Optimizer interface
 */
export function createSpawnOptimizer(config = {}) {
    // Default configuration
    const settings = {
        // How large of an area to sample when checking for flatness
        sampleRadius: 5,
        
        // Maximum allowed slope (0-1, where 0 is flat)
        maxSlope: 0.2,
        
        // Minimum height above water level
        minHeightAboveWater: 2,
        
        // Water level (if not provided)
        waterLevel: 4,
        
        // Maximum attempts to find an optimal spawn point
        maxAttempts: 50,
        
        ...config
    };
    
    /**
     * Find optimal spawn position near a given point
     * @param {Object} basePosition - Starting search position
     * @param {Function} getHeightAt - Function to get terrain height at (x,z)
     * @param {number} waterLevel - Water level to stay above
     * @returns {Object} Optimized spawn position {x, y, z}
     */
    function findOptimalSpawnNear(basePosition, getHeightAt, waterLevel = settings.waterLevel) {
        console.log(`[SpawnOptimizer] Finding optimal spawn near ${basePosition.x}, ${basePosition.z}`);
        
        // If the base position is already good, return it
        if (isGoodSpawnPoint(basePosition, getHeightAt, waterLevel)) {
            const height = getHeightAt(basePosition.x, basePosition.z);
            return { 
                x: basePosition.x,
                y: height + 1,
                z: basePosition.z 
            };
        }
        
        // Otherwise, spiral outward looking for a good spot
        const spiralPositions = generateSpiralPositions(
            basePosition.x, 
            basePosition.z, 
            settings.maxAttempts,
            5 // Step size
        );
        
        for (const pos of spiralPositions) {
            if (isGoodSpawnPoint(pos, getHeightAt, waterLevel)) {
                const height = getHeightAt(pos.x, pos.z);
                return { 
                    x: pos.x,
                    y: height + 1,
                    z: pos.z 
                };
            }
        }
        
        // If no good spot is found, create a plateau at the base position
        console.log(`[SpawnOptimizer] No optimal spawn found, creating plateau at ${basePosition.x}, ${basePosition.z}`);
        
        // The plateau creation would ideally be handled by the terrain system
        // But for now, we just return the base point with a safe height
        const height = Math.max(
            getHeightAt(basePosition.x, basePosition.z),
            waterLevel + settings.minHeightAboveWater
        );
        
        return {
            x: basePosition.x,
            y: height + 1,
            z: basePosition.z,
            isPlateauNeeded: true // Flag to indicate terrain might need modification
        };
    }
    
    /**
     * Check if a point is a good spawn location
     * @param {Object} position - Position to check
     * @param {Function} getHeightAt - Function to get terrain height
     * @param {number} waterLevel - Water level
     * @returns {boolean} Whether this is a good spawn point
     */
    function isGoodSpawnPoint(position, getHeightAt, waterLevel) {
        try {
            // Get height at this point
            const height = getHeightAt(position.x, position.z);
            
            // Check if it's above water
            if (height <= waterLevel + settings.minHeightAboveWater) {
                return false;
            }
            
            // Check flatness by sampling points in a circle
            const samples = [];
            for (let i = 0; i < 8; i++) {
                const angle = (i / 8) * Math.PI * 2;
                const dx = Math.cos(angle) * settings.sampleRadius;
                const dz = Math.sin(angle) * settings.sampleRadius;
                
                const sx = position.x + dx;
                const sz = position.z + dz;
                
                const sampleHeight = getHeightAt(sx, sz);
                samples.push(sampleHeight);
            }
            
            // Calculate slope by finding max height difference
            const minHeight = Math.min(...samples);
            const maxHeight = Math.max(...samples);
            const heightDiff = maxHeight - minHeight;
            
            // Calculate slope as height difference divided by diameter
            const slope = heightDiff / (settings.sampleRadius * 2);
            
            // Check if slope is acceptable
            return slope <= settings.maxSlope;
        } catch (error) {
            console.warn(`[SpawnOptimizer] Error checking spawn point:`, error);
            return false;
        }
    }
    
    /**
     * Generate positions in a spiral pattern
     * @param {number} centerX - Center X
     * @param {number} centerZ - Center Z
     * @param {number} count - Number of positions to generate
     * @param {number} step - Distance between steps
     * @returns {Array} Array of positions
     */
    function generateSpiralPositions(centerX, centerZ, count, step = 5) {
        const positions = [];
        let x = 0;
        let z = 0;
        let segment = 1;
        let dir = 0; // 0: right, 1: up, 2: left, 3: down
        
        for (let i = 0; i < count; i++) {
            // Add current position
            positions.push({ x: centerX + x, z: centerZ + z });
            
            // Move in current direction
            switch (dir) {
                case 0: x += step; break; // Right
                case 1: z -= step; break; // Up
                case 2: x -= step; break; // Left
                case 3: z += step; break; // Down
            }
            
            // Check if we need to change direction
            if (i % segment === 0) {
                dir = (dir + 1) % 4;
                
                // After 2 segments, increase segment length
                if (dir % 2 === 0) {
                    segment++;
                }
            }
        }
        
        return positions;
    }
    
    /**
     * Create a small plateau at the spawn point if needed
     * @param {Object} position - Position to place plateau
     * @param {Function} modifyTerrain - Function to modify terrain
     * MOBILE-FUTURE: Implement when terrain modification API is available
     */
    function createSpawnPlateau(position, modifyTerrain) {
        console.log('[SpawnOptimizer] Plateau creation not yet implemented');
        // This would be implemented when a terrain modification API is available
    }
    
    return {
        findOptimalSpawnNear,
        isGoodSpawnPoint,
        createSpawnPlateau
    };
}

export default createSpawnOptimizer; 